import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Building2, TreePine, X, UserCheck } from "lucide-react";

// ─── Shared constants ────────────────────────────────────────────
const DESIGNATION_MAP = {
  village_accountant: "Village Accountant",
  first_division_clerk: "First Division Clerk",
  second_division_clerk: "Second Division Clerk",
  revenue_inspector: "Revenue Inspector",
  deputy_tahsildar: "Deputy Tahsildar",
  shirestedar: "Shirestedar",
  ddlr: "DDLR",
  adlr: "ADLR",
  surveyor: "Surveyor",
  taluk_surveyor: "Taluk Surveyor",
  case_worker: "Case Worker",
  superintendent: "Superintendent",
  survey_supervisor: "Supervisor",
};

export const SURVEY_WORKFLOW_STATUSES = [
  { value: "submitted", label: "Submitted", label_kn: "ಸಲ್ಲಿಸಲಾಗಿದೆ" },
  { value: "assigned", label: "Assigned", label_kn: "ನಿಯೋಜಿಸಲಾಗಿದೆ" },
  { value: "survey_inspection", label: "Survey Inspection", label_kn: "ಸರ್ವೆ ತಪಾಸಣೆ" },
  { value: "boundary_verification", label: "Boundary Verification", label_kn: "ಗಡಿ ಪರಿಶೀಲನೆ" },
  { value: "sketch_preparation", label: "Sketch Preparation", label_kn: "ನಕ್ಷೆ ತಯಾರಿಕೆ" },
  { value: "order_issued", label: "Order / Endorsement Issued", label_kn: "ಆದೇಶ / ಅನುಮೋದನೆ ಹೊರಡಿಸಲಾಗಿದೆ" },
];

export const REVENUE_WORKFLOW_STATUSES = [
  { value: "submitted", label: "Submitted", label_kn: "ಸಲ್ಲಿಸಲಾಗಿದೆ" },
  { value: "assigned", label: "Assigned", label_kn: "ನಿಯೋಜಿಸಲಾಗಿದೆ" },
  { value: "document_verification", label: "Document Verification", label_kn: "ದಾಖಲೆ ಪರಿಶೀಲನೆ" },
  { value: "field_verification", label: "Field Verification", label_kn: "ಕ್ಷೇತ್ರ ಪರಿಶೀಲನೆ" },
  { value: "under_review", label: "Under Review", label_kn: "ಪರಿಶೀಲನೆಯಲ್ಲಿದೆ" },
  { value: "survey_inspection", label: "Survey Inspection", label_kn: "ಸರ್ವೆ ತಪಾಸಣೆ" },
  { value: "boundary_verification", label: "Boundary Verification", label_kn: "ಗಡಿ ಪರಿಶೀಲನೆ" },
  { value: "sketch_preparation", label: "Sketch Preparation", label_kn: "ನಕ್ಷೆ ತಯಾರಿಕೆ" },
  { value: "order_issued", label: "Order / Endorsement Issued", label_kn: "ಆದೇಶ / ಅನುಮೋದನೆ ಹೊರಡಿಸಲಾಗಿದೆ" },
];

// ─── Shared helpers (exported for external use) ──────────────────
export function getDesignationLabel(official) {
  return DESIGNATION_MAP[official.designation] || official.designation;
}

export function getLocationInfo(official) {
  if (official.designation === "revenue_inspector" && official.assigned_hobli) {
    return { type: "hobli", value: official.assigned_hobli };
  }
  if (official.circle_assignments?.length > 0) {
    const circles = official.circle_assignments
      .map((ca) => (typeof ca === "string" ? ca : ca?.circle || ""))
      .filter(Boolean);
    const value = circles.slice(0, 2).join(", ") + (circles.length > 2 ? "..." : "");
    return { type: "circle", value };
  }
  return null;
}

// ─── Sub-component: a single clickable official row ──────────────
function OfficialRow({ official, isSelected, onSelect, accentColor, testIdPrefix }) {
  const locationInfo = getLocationInfo(official);
  const selectedBg = accentColor === "amber" ? "bg-amber-100 border-amber-500" : "bg-emerald-100 border-emerald-500";
  return (
    <div
      key={official.id}
      onClick={() => onSelect(official.id)}
      className={`p-2 rounded-md cursor-pointer transition-colors ${
        isSelected ? `${selectedBg} border` : "hover:bg-slate-100 border border-transparent"
      }`}
      data-testid={`${testIdPrefix}-${official.id}`}
    >
      <div className="font-medium text-sm">{official.name}</div>
      <div className="text-xs text-slate-500">
        {getDesignationLabel(official)}
        {locationInfo && (
          <span
            className={`ml-1 font-medium ${
              locationInfo.type === "hobli" ? "text-red-600" : "text-blue-600"
            }`}
          >
            &bull; {locationInfo.value}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Sub-component: column of officials ──────────────────────────
function OfficialColumn({ title, icon: Icon, officials, selectedId, onSelect, accentColor, testIdPrefix, emptyMessage, scrollHeight = "h-[220px]" }) {
  const bgHeader = accentColor === "amber" ? "bg-amber-50" : "bg-emerald-50";
  const textColor = accentColor === "amber" ? "text-amber-700" : "text-emerald-700";
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className={`${bgHeader} px-3 py-2 border-b flex items-center gap-2`}>
        <Icon className={`h-4 w-4 ${textColor}`} />
        <span className={`text-sm font-semibold ${textColor}`}>{title}</span>
        <Badge variant="secondary" className="ml-auto text-xs">{officials.length}</Badge>
      </div>
      <ScrollArea className={scrollHeight}>
        <div className="p-2 space-y-1">
          {officials.length === 0 ? (
            <div className="text-center py-4 text-sm text-slate-500">{emptyMessage}</div>
          ) : (
            officials.map((o) => (
              <OfficialRow
                key={o.id}
                official={o}
                isSelected={selectedId === o.id}
                onSelect={onSelect}
                accentColor={accentColor}
                testIdPrefix={testIdPrefix}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Main reusable modal ─────────────────────────────────────────
/**
 * Reusable Assign Official Modal.
 *
 * Props:
 *  open / onOpenChange        – dialog visibility
 *  title / description        – header text
 *  officeOfficials            – pre-filtered list for left column
 *  fieldOfficials             – pre-filtered list for right column
 *  allOfficials               – full list (for name lookup by id)
 *  selectedOfficialId         – currently selected id
 *  onSelectOfficial(id)       – callback when an official is clicked
 *  searchQuery / onSearchChange
 *  assignNote / onAssignNoteChange
 *  assignToStatus / onAssignToStatusChange
 *  workflowStatuses           – array of {value, label, label_kn}
 *  isSurveyUser               – boolean
 *  loading                    – disable confirm button
 *  onConfirm / onCancel
 *  confirmLabel               – button text (default "Assign")
 *  confirmClassName           – button class (default emerald)
 *  testIdPrefix               – prefix for data-testid attributes
 *  currentAssignment          – optional string shown as info banner
 *  emptyFieldMessage          – custom message for empty field column
 *  scrollHeight               – height of scroll areas (default "h-[220px]")
 */
export function AssignOfficialModal({
  open,
  onOpenChange,
  title,
  description,
  officeOfficials,
  fieldOfficials,
  allOfficials,
  selectedOfficialId,
  onSelectOfficial,
  searchQuery,
  onSearchChange,
  assignNote,
  onAssignNoteChange,
  assignToStatus,
  onAssignToStatusChange,
  workflowStatuses,
  isSurveyUser,
  loading = false,
  onConfirm,
  onCancel,
  confirmLabel,
  confirmClassName = "bg-emerald-600 hover:bg-emerald-700",
  testIdPrefix = "assign",
  currentAssignment,
  emptyFieldMessage,
  scrollHeight = "h-[220px]",
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Local state for note to avoid re-rendering entire parent on every keystroke
  const [localNote, setLocalNote] = useState(assignNote || "");

  // Sync local note when parent resets it (e.g. modal open/close)
  useEffect(() => {
    setLocalNote(assignNote || "");
  }, [assignNote]);

  // Flush local note to parent on blur
  const handleNoteBlur = () => {
    if (localNote !== assignNote) {
      onAssignNoteChange(localNote);
    }
  };

  // Use localNote for the confirm button disabled check
  const noteHasValue = localNote.trim().length > 0;

  const noOfficialsMsg = t("officialsManagement.noOfficialsFound") || "No officials found";
  const fieldEmptyMsg = emptyFieldMessage || noOfficialsMsg;

  const selectedName = useMemo(() => {
    if (!selectedOfficialId || !allOfficials) return null;
    return allOfficials.find((o) => o.id === selectedOfficialId)?.name;
  }, [selectedOfficialId, allOfficials]);

  const officeColumnTitle = isSurveyUser
    ? t("officialsManagement.surveyOfficeOfficials") || "Survey Office Officials"
    : t("officialsManagement.talukOfficeOfficials") || "Taluk Office Officials";

  const fieldColumnTitle = t("officialsManagement.fieldOfficeOfficials") || "Field Office Officials";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh]" data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle className="text-emerald-700">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("officialsManagement.searchOfficials") || "Search officials by name..."}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
            data-testid={`${testIdPrefix}-search-input`}
          />
        </div>

        {/* Selected Official */}
        {selectedOfficialId && selectedName && (
          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium">
                {t("officialsManagement.selectedOfficial") || "Selected"}:
              </span>
              <span className="text-sm">{selectedName}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onSelectOfficial("")} className="h-6 w-6 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Two-column layout */}
        <div className="grid grid-cols-2 gap-4">
          <OfficialColumn
            title={officeColumnTitle}
            icon={Building2}
            officials={officeOfficials}
            selectedId={selectedOfficialId}
            onSelect={onSelectOfficial}
            accentColor="emerald"
            testIdPrefix={`${testIdPrefix}-office-official`}
            emptyMessage={noOfficialsMsg}
            scrollHeight={scrollHeight}
          />
          <OfficialColumn
            title={fieldColumnTitle}
            icon={TreePine}
            officials={fieldOfficials}
            selectedId={selectedOfficialId}
            onSelect={onSelectOfficial}
            accentColor="amber"
            testIdPrefix={`${testIdPrefix}-field-official`}
            emptyMessage={fieldEmptyMsg}
            scrollHeight={scrollHeight}
          />
        </div>

        {/* Current assignment info */}
        {currentAssignment && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
            <strong>{t("applicationDetail.currentlyAssigned") || "Currently assigned to"}:</strong> {currentAssignment}
          </div>
        )}

        {/* Status + Note row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("officialsManagement.assigningTo") || "Assigning To (Job Status)"} <span className="text-red-500">*</span>
            </Label>
            <Select value={assignToStatus} onValueChange={onAssignToStatusChange}>
              <SelectTrigger data-testid={`${testIdPrefix}-to-status`} className={`w-full ${!assignToStatus ? "border-red-300" : ""}`}>
                <SelectValue placeholder={t("officialsManagement.selectJobStatus") || "Select job status..."} />
              </SelectTrigger>
              <SelectContent>
                {workflowStatuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {lang === "kn" ? s.label_kn : s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {t("officialsManagement.assigningToHelp") || "Select the workflow stage"}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("officialsManagement.assignNote") || "Assign Note"} <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={localNote}
              onChange={(e) => setLocalNote(e.target.value)}
              onBlur={handleNoteBlur}
              placeholder={t("officialsManagement.assignNotePlaceholder") || "Add notes or instructions..."}
              className={`min-h-[70px] w-full ${!noteHasValue ? "border-red-300" : ""}`}
              data-testid={`${testIdPrefix}-note-input`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid={`${testIdPrefix}-cancel-btn`}>
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={() => {
              // Flush local note to parent before confirming
              if (localNote !== assignNote) onAssignNoteChange(localNote);
              onConfirm();
            }}
            disabled={!selectedOfficialId || !assignToStatus || !noteHasValue || loading}
            className={confirmClassName}
            data-testid={`${testIdPrefix}-confirm-btn`}
          >
            {loading
              ? t("common.processing") || "Processing..."
              : confirmLabel || t("common.assign") || "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
